/**
 * Settings: rebind every control. Click a slot, then press a key or click a
 * mouse button (hold Shift to bind a Shift combination). Escape cancels the
 * capture, or closes the panel when nothing is capturing; the x clears a slot.
 * A binding moves if it was used elsewhere. Saved to localStorage and applied
 * immediately (Controls + HUD labels).
 */

import {
  ACTION_GROUPS, ACTION_LABELS, DEFAULT_KEYMAP, ELEMENT_ACTIONS, bindingLabel, cloneKeymap, keyBinding,
  mouseBinding, saveKeymap, setBinding, settingsKeyAction, shortLabel, type BindAction, type Binding, type Keymap,
} from '../input/keymap.js';
import { button, clear, el, focusPanel, keyboardFocus, restoreFocus, trapTab } from './dom.js';
import type { Screen } from './dom.js';
import { soundSettings } from './soundSettings.js';

export interface SettingsDeps {
  getKeymap(): Readonly<Keymap>;
  setKeymap(map: Keymap): void;
  /** Called with true while the panel is open so the game ignores the keys. */
  onOpenChange(open: boolean): void;
}

export function mountSettings(root: HTMLElement, deps: SettingsDeps): Screen & { open(): void; close(): void } {
  clear(root);
  root.classList.add('settings');
  root.hidden = true;

  const list = el('div', { class: 'settings-list' });
  const status = el('p', { class: 'muted settings-status' }, 'Click a slot, then press a key or click a mouse button. Hold Shift for a Shift combination.');
  const panel = el('div', { class: 'settings-panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Settings', tabindex: -1 },
    el('div', { class: 'settings-head' },
      el('h2', null, 'Controls'),
      button('Reset to defaults', () => apply(cloneKeymap(DEFAULT_KEYMAP)), 'btn btn-ghost'),
      button('Done', () => close(), 'btn btn-primary'),
    ),
    soundSettings(),
    status,
    list,
  );
  root.appendChild(panel);

  let capturing: { action: BindAction; slot: 0 | 1 } | null = null;

  function apply(map: Keymap): void {
    deps.setKeymap(map);
    saveKeymap(storage(), map);
    render();
  }

  function render(): void {
    // The list is rebuilt on every change; a keyboard user keeps their place in it.
    const focused = list.contains(document.activeElement) ? keyboardFocus() : null;
    const focusKey = focused?.dataset['key'];
    clear(list);
    const map = deps.getKeymap();
    for (const group of ACTION_GROUPS) {
      list.appendChild(el('h3', null, group.title));
      for (const action of group.actions) {
        const row = el('div', { class: 'settings-row' }, el('span', { class: 'settings-label' }, ACTION_LABELS[action]));
        for (const slot of [0, 1] as const) {
          const b = map[action][slot];
          const isCap = capturing?.action === action && capturing.slot === slot;
          const slotBtn = button(isCap ? 'Press a key or click...' : (bindingLabel(b) || '+'), (ev) => { ev.stopPropagation(); startCapture(action, slot); }, `btn bind${isCap ? ' capturing' : ''}${b ? '' : ' empty'}`);
          slotBtn.dataset['key'] = `${action}.${slot}.bind`;
          slotBtn.setAttribute('aria-label', `${ACTION_LABELS[action]}, slot ${slot + 1}: ${isCap ? 'press a key or click' : (bindingLabel(b) || 'empty')}`);
          const wrap = el('span', { class: 'bind-slot' }, slotBtn);
          if (b && !isCap) {
            const clearBtn = button('x', () => apply(setBinding(deps.getKeymap(), action, slot, null)), 'btn bind-clear');
            clearBtn.dataset['key'] = `${action}.${slot}.clear`;
            clearBtn.setAttribute('aria-label', `Clear ${ACTION_LABELS[action]}, slot ${slot + 1}`);
            wrap.appendChild(clearBtn);
          }
          row.appendChild(wrap);
        }
        list.appendChild(row);
      }
    }
    if (focusKey) {
      // A cleared slot has no "x" any more: land on its slot button instead.
      const target = list.querySelector<HTMLElement>(`[data-key="${focusKey}"]`) ?? list.querySelector<HTMLElement>(`[data-key="${focusKey.replace(/\.clear$/, '.bind')}"]`);
      target?.focus({ preventScroll: true });
    }
  }

  function startCapture(action: BindAction, slot: 0 | 1): void {
    capturing = { action, slot };
    render();
  }

  function finishCapture(binding: Binding | null): void {
    if (!capturing) return;
    const { action, slot } = capturing;
    capturing = null;
    if (binding) apply(setBinding(deps.getKeymap(), action, slot, binding));
    else render();
  }

  // Capture phase so the press never reaches the game or the button underneath.
  const onKey = (ev: KeyboardEvent): void => {
    if (root.hidden) return;
    const action = settingsKeyAction(capturing !== null, ev.code, ev.shiftKey);
    if (action === 'pass') {
      trapTab(ev, panel);
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    if (action === 'close') close();
    else if (action === 'cancel') finishCapture(null);
    else if (action === 'bind') finishCapture(keyBinding(ev.code, ev.shiftKey));
    // 'wait': a lone modifier; the real key comes next
  };
  const onMouse = (ev: MouseEvent): void => {
    if (root.hidden || !capturing) return;
    // A left click on a slot button is how capture started; only presses after that count.
    ev.preventDefault();
    ev.stopPropagation();
    finishCapture(mouseBinding(ev.button, ev.shiftKey));
  };
  const onContext = (ev: Event): void => { if (!root.hidden && capturing) ev.preventDefault(); };
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('mousedown', onMouse, true);
  window.addEventListener('contextmenu', onContext, true);

  let opener: HTMLElement | null = null;
  function open(): void {
    if (!root.hidden) return;
    opener = keyboardFocus();
    capturing = null;
    render();
    root.hidden = false;
    deps.onOpenChange(true);
    focusPanel(panel);
  }
  function close(): void {
    capturing = null;
    root.hidden = true;
    deps.onOpenChange(false);
    restoreFocus(opener, root);
    opener = null;
  }

  return {
    show: open,
    hide: close,
    open,
    close,
    dispose(): void {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onMouse, true);
      window.removeEventListener('contextmenu', onContext, true);
      clear(root);
    },
  };
}

/** HUD key caps for the eight elements, from the first binding of each. */
export function elementKeyLabels(map: Readonly<Keymap>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of ELEMENT_ACTIONS) out[a] = shortLabel(map[a][0] ?? map[a][1]);
  return out;
}

function storage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
